import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ChevronDown, ChevronRight, Menu, X, LogOut, User } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetTrigger } from '../components/ui/sheet';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const menuItems = [
  { path: '/', label: 'Home' },
  { path: '/freemasonry', label: 'Freemasonry' },
  { path: '/meetings', label: 'Meetings' },
  {
    label: 'University Lodge Scheme',
    children: [
      { path: '/university-lodge-scheme', label: 'Overview' },
      { path: '/university-lodge-scheme/qualifications', label: 'Qualifications' },
    ],
  },
  {
    label: 'More Information',
    children: [
      { path: '/more-information', label: 'General Information' },
      { path: '/contact', label: 'Contact Us' },
    ],
  },
  { path: '/news', label: 'News' },
];

const MenuItem = ({ item, isActive, isMobile, onClose }) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = item.children && item.children.length > 0;

  if (hasChildren) {
    return (
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full flex items-center justify-between px-4 py-3 text-left transition-all duration-200 ${isOpen ? 'bg-gray-100/50 text-gray-900' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/50'
            }`}
          data-testid={`menu-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <span>{item.label}</span>
          {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
        </button>
        {isOpen && (
          <div className="pl-4 border-l border-gray-200 ml-4">
            {item.children.map((child) => (
              <Link
                key={child.path}
                to={child.path}
                onClick={isMobile ? onClose : undefined}
                className="block px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100/50 transition-all duration-200"
                data-testid={`submenu-${child.path.replace(/\//g, '-').slice(1) || 'home'}`}
              >
                {child.label}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <Link
      to={item.path}
      onClick={isMobile ? onClose : undefined}
      className={`block px-4 py-3 transition-all duration-200 ${isActive
        ? 'border-l-4 border-emerald-500 bg-emerald-50/50 text-emerald-900 font-medium'
        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/50'
        }`}
      data-testid={`menu-${item.path.replace(/\//g, '-').slice(1) || 'home'}`}
    >
      {item.label}
    </Link>
  );
};

const Sidebar = ({ isMobile = false, onClose }) => {
  const location = useLocation();

  return (
    <div className={`${isMobile ? '' : 'h-screen sticky top-0 bg-[#f8f9fa] border-r border-gray-200 hidden md:flex flex-col'}`}>
      <div className="p-6 pb-4">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-gray-500">Main Menu</h2>
      </div>
      <nav className="flex-1 overflow-y-auto" data-testid="public-sidebar">
        {menuItems.map((item, index) => (
          <MenuItem
            key={item.path || index}
            item={item}
            isActive={item.path === location.pathname}
            isMobile={isMobile}
            onClose={onClose}
          />
        ))}
        <div className="border-t border-gray-200 mt-4 pt-4">
          <Link
            to="/login"
            onClick={isMobile ? onClose : undefined}
            className={`block px-4 py-3 transition-all duration-200 ${location.pathname === '/login'
              ? 'border-l-4 border-emerald-500 bg-emerald-50/50 text-emerald-900 font-medium'
              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100/50'
              }`}
            data-testid="menu-members-login"
          >
            Members Login
          </Link>
        </div>
      </nav>
    </div>
  );
};

const PublicLayout = ({ children }) => {
  const { isAuthenticated, user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-[#fcfcfc]">
      {/* Header */}
      <header className="bg-[#280a28] text-white" data-testid="main-header">
        <div className="px-6 py-4 md:py-6">
          <div className="flex items-center justify-between">
            {/* Mobile menu button */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="md:hidden">
                <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" data-testid="mobile-menu-toggle">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[280px] p-0 bg-[#f8f9fa]">
                <div className="p-4 border-b border-gray-200">
                  <h2 className="font-heading font-semibold text-lg text-gray-900">Menu</h2>
                </div>
                <Sidebar isMobile onClose={() => setMobileMenuOpen(false)} />
              </SheetContent>
            </Sheet>

            {/* Logo/Title */}
            <div className="text-center flex-1 md:text-left md:flex-none">
              <Link to="/" className="inline-block" data-testid="logo-link">
                <h1 className="font-heading text-lg md:text-xl font-semibold text-emerald-400">
                  St David (University Lodge) No. 36
                </h1>
                <p className="text-xs md:text-sm text-gray-300 mt-0.5">
                  Scotland's first University Scheme Masonic Lodge
                </p>
              </Link>
            </div>

            {/* User menu */}
            <div className="flex items-center gap-2">
              {isAuthenticated ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="text-white hover:bg-white/10 gap-2" data-testid="user-menu-trigger">
                      <User className="h-4 w-4" />
                      <span className="hidden sm:inline">{user?.firstName}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem asChild>
                      <Link to="/members" className="cursor-pointer" data-testid="dropdown-member-area">
                        Member Area
                      </Link>
                    </DropdownMenuItem>
                    {user?.role === 'admin' && (
                      <DropdownMenuItem asChild>
                        <Link to="/admin" className="cursor-pointer" data-testid="dropdown-admin-area">
                          Admin Console
                        </Link>
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={handleLogout} className="cursor-pointer text-red-600" data-testid="dropdown-logout">
                      <LogOut className="h-4 w-4 mr-2" />
                      Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button
                  variant="ghost"
                  className="text-white hover:bg-white/10 hidden md:flex"
                  onClick={() => navigate('/login')}
                  data-testid="header-login-btn"
                >
                  Login
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <div className="grid grid-cols-1 md:grid-cols-[260px_1fr]">
        {/* Sidebar - desktop only */}
        <Sidebar />

        {/* Content */}
        <main className="min-h-[calc(100vh-88px)]" data-testid="main-content">
          {children}
        </main>
      </div>

      {/* Footer */}
      <footer className="bg-[#280a28] text-white py-6 px-6" data-testid="main-footer">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-sm text-gray-300">
            © {new Date().getFullYear()} St David (University Lodge) No. 36. All rights reserved.
          </p>
          <p className="text-xs text-gray-400 mt-2">
            A member of the Scottish University Lodge Scheme of the Grand Lodge of Scotland
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PublicLayout;
